import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { toast } from "sonner";
import { X, Orbit, Loader2, Sparkles, LogIn } from "lucide-react";

export default function AskKundliModal({ question, onClose }) {
  const { t, lang, user } = useApp();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", dob: "", tob: "", place: "", question: question || "" });
  const [loading, setLoading] = useState(false);
  const [answer, setAnswer] = useState("");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async () => {
    if (!form.dob || !form.place || !form.question.trim()) {
      toast.error(lang === "hi" ? "कृपया जन्म तिथि, स्थान और प्रश्न भरें।" : "Please fill date of birth, place and question.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/qa/ask-kundli", { ...form, lang });
      setAnswer(data.answer);
    } catch (err) {
      if (err?.response?.status === 401) { navigate("/login"); return; }
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] grid place-items-center bg-black/70 backdrop-blur-sm px-4 py-8 overflow-y-auto" data-testid="ask-kundli-modal">
      <div className="glass-strong rounded-3xl p-7 max-w-lg w-full relative rise-in my-auto">
        <button onClick={onClose} className="absolute top-4 right-4 text-slate-400 hover:text-amber-300" data-testid="ask-kundli-close"><X size={20} /></button>
        <div className="flex items-center gap-3 mb-2">
          <div className="h-11 w-11 rounded-xl grid place-items-center bg-gradient-to-br from-amber-500/25 to-purple-600/25 border border-amber-500/30">
            <Orbit className="text-amber-300" size={22} />
          </div>
          <h3 className="font-serif text-xl text-amber-100">{t.ask.kundliTitle}</h3>
        </div>
        <p className="text-sm text-slate-400 mb-5">{t.ask.kundliDesc}</p>

        {!user ? (
          <div className="text-center py-6">
            <p className="text-slate-300 mb-4">{t.ask.loginRequired}</p>
            <button onClick={() => navigate("/login")} className="btn-gold rounded-full px-6 py-3 inline-flex items-center gap-2">
              <LogIn size={16} /> {t.nav.login}
            </button>
          </div>
        ) : answer ? (
          <div data-testid="ask-kundli-answer">
            <div className="text-xs uppercase tracking-widest text-amber-300/70 mb-2 flex items-center gap-1.5"><Sparkles size={13} /> {t.ask.answerTitle}</div>
            <div className="glass rounded-2xl p-5 text-slate-200 leading-relaxed whitespace-pre-line">{answer}</div>
            <p className="mt-3 text-xs text-slate-500">{t.ask.disclaimer}</p>
            <div className="flex gap-3 mt-5">
              <button onClick={() => setAnswer("")} className="btn-ghost-gold rounded-full px-5 py-2.5 text-sm">{t.ask.askAgain}</button>
              <button onClick={onClose} className="btn-gold rounded-full px-5 py-2.5 text-sm">{t.ask.close}</button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <input value={form.name} onChange={set("name")} placeholder={t.ask.name} data-testid="kundli-name" className="w-full rounded-xl bg-purple-950/50 border border-amber-500/25 px-4 py-3 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-amber-400/60" />
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-amber-300/70">{t.ask.dob}</label>
                <input type="date" value={form.dob} onChange={set("dob")} data-testid="kundli-dob" className="w-full mt-1 rounded-xl bg-purple-950/50 border border-amber-500/25 px-3 py-2.5 text-sm text-slate-100 focus:outline-none focus:border-amber-400/60" />
              </div>
              <div>
                <label className="text-xs text-amber-300/70">{t.ask.tob}</label>
                <input type="time" value={form.tob} onChange={set("tob")} data-testid="kundli-tob" className="w-full mt-1 rounded-xl bg-purple-950/50 border border-amber-500/25 px-3 py-2.5 text-sm text-slate-100 focus:outline-none focus:border-amber-400/60" />
              </div>
            </div>
            <input value={form.place} onChange={set("place")} placeholder={t.ask.place} data-testid="kundli-place" className="w-full rounded-xl bg-purple-950/50 border border-amber-500/25 px-4 py-3 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-amber-400/60" />
            <textarea value={form.question} onChange={set("question")} placeholder={t.ask.yourQuestion} rows={3} data-testid="kundli-question" className="w-full rounded-xl bg-purple-950/50 border border-amber-500/25 px-4 py-3 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-amber-400/60 resize-none" />
            <button onClick={submit} disabled={loading} data-testid="kundli-submit" className="btn-gold w-full rounded-full py-3.5 flex items-center justify-center gap-2 gold-glow disabled:opacity-70">
              {loading ? <><Loader2 className="animate-spin" size={18} /> {t.ask.generating}</> : <><Sparkles size={18} /> {t.ask.generate}</>}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
